<template>
  <div class="analytics-vendas animated fadeIn">
    <!-- ─── HEADER ─── -->
    <div class="report-header-premium mb-4 rounded shadow-sm d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-3 p-md-4">
      <div class="report-header-premium__info d-flex align-items-center mb-3 mb-md-0">
        <div class="report-header-premium__icon d-none d-sm-flex">
          <i class="fa fa-line-chart text-white"></i>
        </div>
        <div class="ml-sm-3">
          <h4 class="report-header-premium__title text-white mb-1">Analytics de Vendas</h4>
          <div class="d-flex align-items-center">
            <span class="report-header-premium__sub text-white-50">Resumo Diário de Performance e Margens</span>
            <b-badge variant="success" pill class="ml-2 py-1 px-2 border border-white-50 shadow-sm" style="font-size: 0.65rem;">
              <i class="fa fa-trophy mr-1"></i> PREMIUM
            </b-badge>
          </div>
        </div>
      </div>
      
      <!-- BOTÕES -->
      <div class="d-flex align-items-center flex-wrap gap-2 position-relative z-index-1">
        <b-button variant="outline-light" size="sm" class="mr-2 btn-imprimir" @click="imprimir" v-b-tooltip.hover title="Imprimir Tela / Dashboard">
          <i class="fa fa-desktop"></i>
        </b-button>
        <b-button variant="light" size="sm" class="mr-2 btn-imprimir font-weight-bold" @click="imprimirRelatorio" v-b-tooltip.hover title="Imprimir Relatório Padrão (A4)">
          <i class="fa fa-print mr-1"></i> A4
        </b-button>
        <b-button variant="success" size="sm" class="mr-3 btn-imprimir border-white-50 shadow-sm font-weight-bold" @click="exportarCsv" v-b-tooltip.hover title="Exportar para Excel (CSV)">
          <i class="fa fa-file-excel-o mr-1"></i> EXCEL
        </b-button>
      </div>
        
      <!-- FILTROS -->
      <div class="d-none d-md-flex align-items-center position-relative z-index-1 mt-3 mt-md-0">
        <div class="filter-pills d-flex align-items-center bg-white-10 rounded-pill px-3 py-2">
          <b-form-select 
            v-model="filtro.visao" 
            :options="visaoOptions"
            class="filter-select form-control-sm mr-3 shadow-none font-weight-bold"
            style="width: 180px; background-color: #374151; color: #ffffff; border: 1px solid rgba(255,255,255,0.2);"
          ></b-form-select>

          <span class="text-white-50 small font-weight-bold mr-2 text-uppercase d-none d-lg-inline">Período:</span>
          <b-form-input 
            type="date" 
            v-model="filtro.dt1" 
            class="filter-date form-control-sm mr-2 border-0 shadow-none text-white font-weight-bold"
            style="width: 130px; background-color: rgba(255,255,255,0.15);"
          ></b-form-input>
          <span class="text-white-50 mx-1">até</span>
          <b-form-input 
            type="date" 
            v-model="filtro.dt2" 
            class="filter-date form-control-sm ml-2 border-0 shadow-none text-white font-weight-bold"
            style="width: 130px; background-color: rgba(255,255,255,0.15);"
          ></b-form-input>
        </div>

        <b-button variant="primary" pill class="ml-3 px-4 font-weight-bold shadow-sm" @click="buscarDados" :disabled="loading">
          <b-spinner small v-if="loading" class="mr-2"></b-spinner>
          <i class="fa fa-search mr-2" v-else></i> BUSCAR
        </b-button>
      </div>
    </div>

    <!-- ─── CARDS DE TOTAIS ─── -->
    <b-row class="mb-4 d-print-none" v-if="dados.length > 0">
      <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
        <div class="kpi-card-premium p-3 rounded shadow-sm border-left-primary h-100" :class="isDark ? 'bg-gray-800' : 'bg-white'">
          <small class="text-muted text-uppercase font-weight-bold">Peso Total</small>
          <h3 class="mb-0 text-primary mt-1">{{ formatNumber(totais.peso) }} KG</h3>
        </div>
      </b-col>
      <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
        <div class="kpi-card-premium p-3 rounded shadow-sm border-left-info h-100" :class="isDark ? 'bg-gray-800' : 'bg-white'">
          <small class="text-muted text-uppercase font-weight-bold">Faturamento Bruto</small>
          <h3 class="mb-0 text-info mt-1">{{ formatBRL(totais.bruto) }}</h3>
        </div>
      </b-col>
      <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
        <div class="kpi-card-premium p-3 rounded shadow-sm border-left-warning h-100" :class="isDark ? 'bg-gray-800' : 'bg-white'">
          <small class="text-muted text-uppercase font-weight-bold">Comissão Total</small>
          <h3 class="mb-0 text-warning mt-1">{{ formatBRL(totais.comissao) }}</h3>
        </div>
      </b-col>
      <b-col sm="6" lg="3" class="mb-3 mb-lg-0">
        <div class="kpi-card-premium p-3 rounded shadow-sm border-left-success h-100" :class="isDark ? 'bg-gray-800' : 'bg-white'">
          <small class="text-muted text-uppercase font-weight-bold">Lucro Consolidado</small>
          <h3 class="mb-0 mt-1" :class="totais.liquido >= 0 ? 'text-success' : 'text-danger'">{{ formatBRL(totais.liquido) }}</h3>
        </div>
      </b-col>
    </b-row>

    <!-- ─── GRÁFICO DE DESEMPENHO ─── -->
    <b-card class="border-0 shadow-sm mb-4 print-chart-card" :bg-variant="isDark ? 'gray-800' : 'white'" v-show="dados.length > 0">
      <template #header>
        <div class="d-flex align-items-center">
          <i class="fa fa-bar-chart mr-2 text-primary"></i>
          <span class="font-weight-bold" :class="isDark ? 'text-white' : ''">
            {{ tituloGrafico }}
          </span>
        </div>
      </template>
      <div ref="chartDom" style="width: 100%; height: 350px;"></div>
    </b-card>

    <!-- ─── TABELA DE DADOS ─── -->
    <b-card class="border-0 shadow-sm mb-4 table-card" :bg-variant="isDark ? 'gray-800' : 'white'">
      <template #header>
        <div class="d-flex align-items-center">
          <i class="fa fa-table mr-2 text-primary"></i>
          <span class="font-weight-bold" :class="isDark ? 'text-white' : ''">Detalhamento das Vendas</span>
        </div>
      </template>
      
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" label="Carregando..."></b-spinner>
        <div class="mt-2 text-muted">Processando dados de vendas...</div>
      </div>
      
      <div v-else-if="dados.length === 0" class="text-center py-5">
        <i class="fa fa-inbox fa-3x text-muted mb-3 opacity-50"></i>
        <h5 class="text-muted">Nenhum dado encontrado para o período.</h5>
        <p class="text-muted small">Ajuste os filtros de data e clique em Buscar.</p>
      </div>

      <div class="table-responsive" v-else>
        <table class="table table-hover table-striped table-bordered text-center align-middle mb-0" :class="isDark ? 'table-dark' : ''">
          <thead :class="isDark ? 'bg-dark' : 'bg-light'">
            <tr>
              <th class="text-left" v-if="mostraData">DATA</th>
              <th class="text-left" v-if="mostraData">DESCRIÇÃO</th>
              <th class="text-left" v-if="mostraProduto">PRODUTO</th>
              <th class="text-right">PESO</th>
              <th class="text-right">VALOR BRUTO</th>
              <th class="text-right">VALOR COMISSÃO</th>
              <th class="text-center">% COMISSÃO</th>
              <th class="text-right">LUCRO CONSOLIDADO</th>
              <th class="text-center">% LUCRO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, idx) in dados" :key="idx">
              <td class="text-left font-weight-bold" v-if="mostraData">{{ formatData(item.DATA) }}</td>
              <td class="text-left" v-if="mostraData">{{ item.DESCRICAO }}</td>
              <td class="text-left" v-if="mostraProduto">{{ item.NOME_PRODUTO }}</td>
              <td class="text-right">{{ formatNumber(item.PESO) }}</td>
              <td class="text-right text-primary font-weight-bold">{{ formatBRL(item.VALOR_BRUTO) }}</td>
              <td class="text-right text-warning font-weight-bold">{{ formatBRL(item.VALOR_COMISSAO) }}</td>
              <td class="text-center">
                <b-badge variant="warning" class="px-2">{{ calcularPercentual(item.VALOR_COMISSAO, item.VALOR_BRUTO) }}%</b-badge>
              </td>
              <td class="text-right font-weight-bold" :class="Number(item.VALOR_LIQUIDO) >= 0 ? 'text-success' : 'text-danger'">
                {{ formatBRL(item.VALOR_LIQUIDO) }}
              </td>
              <td class="text-center">
                <b-badge :variant="Number(item.VALOR_LIQUIDO) >= 0 ? 'success' : 'danger'" class="px-2">
                  {{ calcularPercentual(item.VALOR_LIQUIDO, item.VALOR_BRUTO) }}%
                </b-badge>
              </td>
            </tr>
          </tbody>
          <tfoot class="font-weight-bold bg-primary text-white">
            <tr>
              <td :colspan="colspanTfoot" class="text-right">TOTAIS ({{ dados.length }} linhas):</td>
              <td class="text-right">{{ formatNumber(totais.peso) }}</td>
              <td class="text-right">{{ formatBRL(totais.bruto) }}</td>
              <td class="text-right">{{ formatBRL(totais.comissao) }}</td>
              <td class="text-center">{{ calcularPercentual(totais.comissao, totais.bruto) }}%</td>
              <td class="text-right">{{ formatBRL(totais.liquido) }}</td>
              <td class="text-center">{{ calcularPercentual(totais.liquido, totais.bruto) }}%</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </b-card>

  </div>
</template>

<script>
import moment from 'moment'
import * as echarts from 'echarts'
import { printGroupedHtmlReport } from '@/utils/RelatorioHTML'

export default {
  name: 'AnalyticsVendas',
  data() {
    return {
      loading: false,
      dados: [],
      chart: null,
      filtro: {
        visao: 'dia',
        dt1: moment().startOf('month').format('YYYY-MM-DD'),
        dt2: moment().endOf('month').format('YYYY-MM-DD')
      },
      visaoOptions: [
        { value: 'dia', text: 'Visão Diária' },
        { value: 'produto', text: 'Visão por Produto' },
        { value: 'diaproduto', text: 'Detalhada (Dia + Prod)' }
      ]
    }
  },
  computed: {
    isDark() { return this.$store.getters['tema/IS_DARK'] },
    mostraData() { return ['dia', 'diaproduto'].includes(this.filtro.visao) },
    mostraProduto() { return ['produto', 'diaproduto'].includes(this.filtro.visao) },
    colspanTfoot() {
      if (this.filtro.visao === 'diaproduto') return 3
      if (this.filtro.visao === 'dia') return 2
      return 1 // produto
    },
    tituloGrafico() {
      if (this.filtro.visao === 'produto') return 'Top 10 Produtos (Lucro Consolidado)'
      return 'Evolução Diária (Faturamento x Peso)'
    },
    totais() {
      return this.dados.reduce((acc, item) => {
        acc.peso += Number(item.PESO || 0)
        acc.bruto += Number(item.VALOR_BRUTO || 0)
        acc.comissao += Number(item.VALOR_COMISSAO || 0)
        acc.liquido += Number(item.VALOR_LIQUIDO || 0)
        return acc
      }, { peso: 0, bruto: 0, comissao: 0, liquido: 0 })
    }
  },
  mounted() {
    this.buscarDados()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) this.chart.dispose()
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    resizeChart() {
      if (this.chart) {
        this.chart.resize()
      }
    },
    renderChart() {
      if (!this.$refs.chartDom) return
      
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chartDom)
      }

      this.chart.clear()

      let option = {}

      if (this.filtro.visao === 'produto') {
        // Grafico de Barras Horizontais: Top 10 Produtos por Lucro
        // Pega os 10 primeiros da tabela (já ordenados pelo backend por Lucro DESC)
        const top10 = this.dados.slice(0, 10).reverse() // inverte para desenhar do maior no topo
        
        const yAxisData = top10.map(item => {
          let nome = item.NOME_PRODUTO || 'Desconhecido'
          if (nome.length > 20) nome = nome.substring(0, 20) + '...'
          return nome
        })
        const lucroData = top10.map(item => Number(item.VALOR_LIQUIDO || 0))

        option = {
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          legend: { show: false },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          xAxis: { type: 'value', axisLabel: { formatter: 'R$ {value}' } },
          yAxis: { 
            type: 'category', 
            data: yAxisData,
            axisLabel: { color: this.isDark ? '#fff' : '#333' }
          },
          series: [
            {
              name: 'Lucro',
              type: 'bar',
              itemStyle: {
                color: (params) => {
                  return params.value >= 0 ? '#4dbd74' : '#f86c6b'
                },
                borderRadius: [0, 4, 4, 0]
              },
              label: {
                show: true,
                position: 'right',
                formatter: (p) => 'R$ ' + p.value.toLocaleString('pt-BR', {minimumFractionDigits:2, maximumFractionDigits:2})
              },
              data: lucroData
            }
          ]
        }
      } else {
        // Gráfico Original: Evolução Diária (Linha + Barra)
        const reverseData = [...this.dados].reverse()
        const xAxisData = reverseData.map(item => this.formatData(item.DATA).substring(0,5))
        const faturamentoData = reverseData.map(item => Number(item.VALOR_BRUTO || 0))
        const pesoData = reverseData.map(item => Number(item.PESO || 0))

        option = {
          tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
          legend: {
            data: ['Faturamento', 'Peso (KG)'],
            textStyle: { color: this.isDark ? '#fff' : '#333' }
          },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          xAxis: [
            {
              type: 'category',
              data: xAxisData,
              axisPointer: { type: 'shadow' }
            }
          ],
          yAxis: [
            {
              type: 'value',
              name: 'Faturamento',
              axisLabel: { formatter: 'R$ {value}' }
            },
            {
              type: 'value',
              name: 'Peso (KG)',
              axisLabel: { formatter: '{value} KG' }
            }
          ],
          series: [
            {
              name: 'Faturamento',
              type: 'bar',
              itemStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: '#20a8d8' },
                  { offset: 1, color: '#187da0' }
                ]),
                borderRadius: [4, 4, 0, 0]
              },
              data: faturamentoData
            },
            {
              name: 'Peso (KG)',
              type: 'line',
              yAxisIndex: 1,
              smooth: true,
              symbol: 'circle',
              symbolSize: 8,
              itemStyle: { color: '#f8cb00' },
              lineStyle: { width: 3, shadowColor: 'rgba(0,0,0,0.3)', shadowBlur: 10 },
              data: pesoData
            }
          ]
        }
      }

      this.chart.setOption(option)
    },
    formatBRL(v) {
      return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    },
    formatNumber(v) {
      return Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatData(d) {
      if (!d) return ''
      return moment(d).format('DD/MM/YYYY')
    },
    calcularPercentual(parte, total) {
      if (!total || total === 0) return '0.00'
      return ((Number(parte) / Number(total)) * 100).toFixed(2)
    },
    async buscarDados() {
      this.loading = true
      try {
        const uri = `/api/protected/venda/resumo-diario-analytics?dt1=${this.filtro.dt1}&dt2=${this.filtro.dt2}&visao=${this.filtro.visao}`
        const res = await this.$http.get(uri)
        this.dados = res.data || []
        this.$nextTick(() => {
          if (this.dados.length > 0) {
            this.renderChart()
          } else if (this.chart) {
            this.chart.clear()
          }
        })
      } catch (e) {
        console.error(e)
        this.$toast.error({
          title: 'Erro',
          message: 'Falha ao buscar dados de vendas.',
          position: 'top center'
        })
      } finally {
        this.loading = false
      }
    },
    imprimir(event) {
      this.$root.$emit('bv::hide::tooltip')
      if (event && event.currentTarget) event.currentTarget.blur()
      
      setTimeout(() => {
        window.print()
      }, 300)
    },
    imprimirRelatorio(event) {
      this.$root.$emit('bv::hide::tooltip')
      if (event && event.currentTarget) event.currentTarget.blur()

      if (this.dados.length === 0) {
        this.$toast.warning({ title: 'Aviso', message: 'Nenhum dado para imprimir.' })
        return
      }

      let colunas = []
      if (this.mostraData) {
        colunas.push(
          { key: 'DATA', label: 'Data', width: '100px', align: 'center', formatter: (v) => this.formatData(v) },
          { key: 'DESCRICAO', label: 'Descrição', width: '200px' }
        )
      }
      if (this.mostraProduto) {
        colunas.push({ key: 'NOME_PRODUTO', label: 'Produto', width: '350px' })
      }
      colunas.push(
        { key: 'PESO', label: 'Peso', width: '100px', align: 'right', type: 'number' },
        { key: 'VALOR_BRUTO', label: 'V. Bruto (R$)', width: '120px', align: 'right', type: 'currency' },
        { key: 'VALOR_COMISSAO', label: 'Comissão (R$)', width: '120px', align: 'right', type: 'currency' },
        { 
          key: 'PERC_COMISSAO', 
          label: '% Comis.', 
          width: '80px', 
          align: 'center', 
          get: (r) => this.calcularPercentual(r.VALOR_COMISSAO, r.VALOR_BRUTO) + '%',
          summary: (rows) => {
            const totalBruto = rows.reduce((acc, item) => acc + Number(item.VALOR_BRUTO || 0), 0)
            const totalComissao = rows.reduce((acc, item) => acc + Number(item.VALOR_COMISSAO || 0), 0)
            return this.calcularPercentual(totalComissao, totalBruto) + '%'
          }
        },
        { key: 'VALOR_LIQUIDO', label: 'Lucro (R$)', width: '120px', align: 'right', type: 'currency' },
        { 
          key: 'PERC_LUCRO', 
          label: '% Lucro', 
          width: '80px', 
          align: 'center', 
          get: (r) => this.calcularPercentual(r.VALOR_LIQUIDO, r.VALOR_BRUTO) + '%',
          summary: (rows) => {
            const totalBruto = rows.reduce((acc, item) => acc + Number(item.VALOR_BRUTO || 0), 0)
            const totalLiquido = rows.reduce((acc, item) => acc + Number(item.VALOR_LIQUIDO || 0), 0)
            return this.calcularPercentual(totalLiquido, totalBruto) + '%'
          }
        }
      )

      const visaoTxt = this.visaoOptions.find(v => v.value === this.filtro.visao)?.text || 'Visão'
      const obs = `Período: ${this.formatData(this.filtro.dt1)} até ${this.formatData(this.filtro.dt2)}\nAgrupamento: ${visaoTxt}`
      const tipoLinha = this.filtro.visao === 'produto' ? 'produtos' : (this.filtro.visao === 'dia' ? 'dias' : 'registros')

      printGroupedHtmlReport({
        reportTitle: 'Relatório Analytics de Vendas',
        observations: obs,
        columns: colunas,
        rows: this.dados,
        sumKeys: ['PESO', 'VALOR_BRUTO', 'VALOR_COMISSAO', 'VALOR_LIQUIDO', 'PERC_COMISSAO', 'PERC_LUCRO'],
        landscape: this.mostraProduto && this.mostraData,
        systemName: window.localStorage.getItem('nomeempresa') || 'CEC WEB',
        printedBy: window.localStorage.getItem('nome_usuario') || 'Usuário',
        pageSize: 'A4',
        footerLabel: `TOTAIS (${this.dados.length} ${tipoLinha}):`
      })
    },
    exportarCsv(event) {
      this.$root.$emit('bv::hide::tooltip')
      if (event && event.currentTarget) event.currentTarget.blur()

      if (this.dados.length === 0) return
      
      let csvContent = "data:text/csv;charset=utf-8,\uFEFF"
      
      // Montar cabeçalhos baseados na visão
      let colunas = []
      if (this.mostraData) { colunas.push("DATA", "DESCRIÇÃO") }
      if (this.mostraProduto) { colunas.push("PRODUTO") }
      colunas.push("PESO", "VALOR BRUTO", "VALOR COMISSÃO", "% COMISSÃO", "LUCRO CONSOLIDADO", "% LUCRO")
      
      csvContent += colunas.join(";") + "\r\n"
      
      this.dados.forEach(item => {
        let row = []
        if (this.mostraData) { 
          row.push(this.formatData(item.DATA), item.DESCRICAO) 
        }
        if (this.mostraProduto) { 
          row.push(item.NOME_PRODUTO) 
        }
        
        row.push(
          Number(item.PESO || 0).toFixed(2).replace('.', ','),
          Number(item.VALOR_BRUTO || 0).toFixed(2).replace('.', ','),
          Number(item.VALOR_COMISSAO || 0).toFixed(2).replace('.', ','),
          this.calcularPercentual(item.VALOR_COMISSAO, item.VALOR_BRUTO).replace('.', ','),
          Number(item.VALOR_LIQUIDO || 0).toFixed(2).replace('.', ','),
          this.calcularPercentual(item.VALOR_LIQUIDO, item.VALOR_BRUTO).replace('.', ',')
        )
        csvContent += row.join(";") + "\r\n"
      })
      
      // Adiciona Totais no final
      let totaisRow = []
      totaisRow.push("TOTAIS:")
      
      // Preencher espaços vazios baseados no colspan
      for(let i=1; i < this.colspanTfoot; i++) {
        totaisRow.push("")
      }
      
      totaisRow.push(
        this.totais.peso.toFixed(2).replace('.', ','),
        this.totais.bruto.toFixed(2).replace('.', ','),
        this.totais.comissao.toFixed(2).replace('.', ','),
        this.calcularPercentual(this.totais.comissao, this.totais.bruto).replace('.', ','),
        this.totais.liquido.toFixed(2).replace('.', ','),
        this.calcularPercentual(this.totais.liquido, this.totais.bruto).replace('.', ',')
      )
      
      csvContent += totaisRow.join(";") + "\r\n"
      
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement("a")
      link.setAttribute("href", encodedUri)
      link.setAttribute("download", `analytics_vendas_${this.filtro.visao}_${moment().format('YYYYMMDD')}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.report-header-premium {
  background: linear-gradient(135deg, #111827 0%, #374151 100%);
  border-left: 5px solid #20a8d8;
}

.report-header-premium__icon {
  width: 54px;
  height: 54px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.bg-white-10 {
  background-color: rgba(255, 255, 255, 0.1);
}

.filter-pills select.filter-select {
  cursor: pointer;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23FFFFFF%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E");
  background-repeat: no-repeat;
  background-position: right 0.7rem top 50%;
  background-size: 0.65rem auto;
}

.filter-pills input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
  cursor: pointer;
}



.border-left-primary { border-left: 4px solid #20a8d8 !important; }
.border-left-info { border-left: 4px solid #63c2de !important; }
.border-left-warning { border-left: 4px solid #f8cb00 !important; }
.border-left-success { border-left: 4px solid #4dbd74 !important; }

@media print {
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  
  .analytics-vendas {
    background-color: transparent !important;
  }

  .filter-pills, .btn-imprimir, .btn, .app-header, .sidebar, .app-footer {
    display: none !important;
  }

  .report-header-premium {
    background: linear-gradient(135deg, #111827 0%, #374151 100%) !important;
    border-left: 5px solid #20a8d8 !important;
    padding: 15px !important;
    margin-bottom: 20px !important;
    border-radius: 8px !important;
  }

  .report-header-premium__title {
    color: white !important;
  }

  .kpi-card-premium {
    background-color: white !important;
    box-shadow: 0 2px 5px rgba(0,0,0,0.1) !important;
    margin-bottom: 15px !important;
  }

  .table-card {
    box-shadow: none !important;
    border: 1px solid #e0e0e0 !important;
  }

  .table {
    border: 1px solid #ccc !important;
    width: 100% !important;
  }
  
  .table th, .table td {
    border: 1px solid #ccc !important;
    padding: 0.35rem !important;
    font-size: 11px !important;
    color: #000 !important;
  }

  .table thead {
    background-color: #f8f9fa !important;
  }

  .main {
    margin-left: 0 !important;
    padding: 0 !important;
    background-color: white !important;
  }

  /* Ajuste de Quebra de Página para o Gráfico */
  .print-chart-card {
    page-break-inside: avoid;
    margin-bottom: 30px !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/estrategia/AnalyticsVendas.vue